/**
 * Parse a raw Overpass API JSON response into structured data for all rendered elements.
 *
 * Returns:
 *   roads[]      – { id, highway, name, oneway, width, bridge, layer, nodes }
 *   buildings[]  – { id, height, buildingType, wallColor, roofColor, …, nodes, holes }
 *   areas[]      – { id, areaType, nodes }
 *   trees[]      – { id, lat, lon, height, leafType }      (node features)
 *   treeRows[]   – { id, nodes }                           (way features)
 *   railways[]   – { id, railType, bridge, layer, nodes }
 *   barriers[]   – { id, barrierType, nodes }
 *   fountains[]  – { id, lat, lon }                        (node features)
 */

// Highway values to skip (non-drivable / irrelevant)
const IGNORED_HIGHWAY = new Set([
  'footway', 'steps', 'elevator', 'corridor',
  'proposed', 'construction', 'abandoned',
  'platform', 'raceway', 'escape', 'bus_guideway',
]);

// Railway values to render
const RAILWAY_TYPES = new Set([
  'rail', 'tram', 'light_rail', 'narrow_gauge', 'subway',
]);

// Barrier values to render
const BARRIER_TYPES = new Set([
  'wall', 'fence', 'hedge', 'retaining_wall', 'guard_rail',
]);

// ── Area tag sets ──────────────────────────────────────────────────────────────

const AREA_LANDUSE = new Set([
  'forest', 'wood', 'grass', 'meadow', 'village_green', 'park', 'garden',
  'recreation_ground', 'farmland', 'farmyard', 'allotments', 'orchard', 'vineyard',
  'cemetery', 'residential', 'commercial', 'industrial', 'retail',
  'construction', 'brownfield', 'greenfield', 'military',
  'basin', 'reservoir', 'pond', 'water', 'beach', 'sand',
]);

const AREA_NATURAL = new Set([
  'wood', 'scrub', 'heath', 'grassland', 'tundra',
  'water', 'wetland', 'beach', 'sand', 'bare_rock', 'scree',
]);

const AREA_LEISURE = new Set([
  'park', 'garden', 'nature_reserve', 'common',
  'pitch', 'track', 'golf_course',
  'playground', 'recreation_ground', 'sports_centre',
]);

// ── Main parser ────────────────────────────────────────────────────────────────

export function parseOSMData(osmJson) {
  if (!osmJson?.elements) {
    return { roads: [], buildings: [], areas: [], trees: [], treeRows: [], railways: [], barriers: [], fountains: [], coastlines: [], trafficSignals: [] };
  }

  // 1. Build a node lookup map: id → {lat, lon}
  const nodeMap = new Map();
  for (const el of osmJson.elements) {
    if (el.type === 'node' && el.lat !== undefined) {
      nodeMap.set(el.id, { lat: el.lat, lon: el.lon });
    }
  }

  // 2. Build a way lookup map: id → way (for relation members)
  const wayMap = new Map();
  for (const el of osmJson.elements) {
    if (el.type === 'way') wayMap.set(el.id, el);
  }

  const roads          = [];
  const buildings      = [];
  const areas          = [];
  const trees          = [];
  const treeRows       = [];
  const railways       = [];
  const barriers       = [];
  const fountains      = [];
  const coastlines     = [];
  const trafficSignals = [];

  for (const el of osmJson.elements) {

    // ── Node features ──────────────────────────────────────────────────────────
    if (el.type === 'node' && el.lat !== undefined) {
      const tags = el.tags || {};

      if (tags.natural === 'tree') {
        trees.push({
          id:       el.id,
          lat:      el.lat,
          lon:      el.lon,
          height:   parseFloat(tags.height) || null,
          leafType: tags.leaf_type || null, // 'broadleaved' | 'needleleaved'
        });
      }

      if (tags.amenity === 'fountain') {
        fountains.push({ id: el.id, lat: el.lat, lon: el.lon });
      }

      if (tags.highway === 'traffic_signals') {
        trafficSignals.push({ id: el.id, lat: el.lat, lon: el.lon });
      }
    }

    // ── Way features ───────────────────────────────────────────────────────────
    if (el.type === 'way') {
      const tags = el.tags || {};

      // --- Roads ---
      if (tags.highway && !IGNORED_HIGHWAY.has(tags.highway)) {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 2) {
          roads.push({
            id:      el.id,
            highway: tags.highway,
            name:    tags.name || null,
            oneway:  tags.oneway === 'yes',
            width:   parseFloat(tags.width) || null,
            bridge:  tags.bridge === 'yes',
            layer:   parseInt(tags.layer) || 0,
            nodes,
          });
        }
      }

      // --- Railways ---
      if (tags.railway && RAILWAY_TYPES.has(tags.railway)) {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 2) {
          railways.push({
            id:       el.id,
            railType: tags.railway,
            bridge:   tags.bridge === 'yes',
            layer:    parseInt(tags.layer) || 0,
            nodes,
          });
        }
      }

      // --- Barriers ---
      if (tags.barrier && BARRIER_TYPES.has(tags.barrier)) {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 2) {
          barriers.push({
            id:          el.id,
            barrierType: tags.barrier,
            nodes,
          });
        }
      }

      // --- Coastlines ---
      if (tags.natural === 'coastline') {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 2) coastlines.push({ id: el.id, nodes });
      }

      // --- Tree rows (already fetched by way["natural"]) ---
      if (tags.natural === 'tree_row') {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 2) {
          treeRows.push({ id: el.id, nodes });
        }
      }

      // --- Ground areas (landuse / natural / leisure) ---
      const isClosed = el.nodes?.length >= 4 &&
                       el.nodes[0] === el.nodes[el.nodes.length - 1];
      if (isClosed) {
        const areaType = _resolveAreaType(tags);
        if (areaType) {
          const nodes = resolveNodes(el.nodes, nodeMap);
          if (nodes.length >= 3) {
            areas.push({ id: el.id, areaType, nodes });
          }
        }
      }

      // --- Simple (non-multipolygon) buildings ---
      if (tags.building && !tags['type']) {
        const nodes = resolveNodes(el.nodes, nodeMap);
        if (nodes.length >= 3) {
          buildings.push({
            id:           el.id,
            height:       extractHeight(tags),
            buildingType: tags.building,
            wallColor:    tags['building:colour'] || tags['building:color'] || null,
            roofColor:    tags['roof:colour']     || tags['roof:color']     || null,
            roofShape:    tags['roof:shape']      || null,
            roofHeight:   parseFloat(tags['roof:height']) || null,
            roofMaterial: tags['roof:material']   || null,
            buildingMat:  tags['building:material'] || null,
            nodes,
            holes: [],
          });
        }
      }
    }

    // ── Multipolygon buildings (relations) ─────────────────────────────────────
    if (el.type === 'relation' && el.tags?.building) {
      const outerWays = [];
      const innerWays = [];

      for (const member of el.members || []) {
        if (member.type !== 'way') continue;
        const way = wayMap.get(member.ref);
        if (!way) continue;
        const nodes = resolveNodes(way.nodes, nodeMap);
        if (nodes.length < 2) continue;

        if (member.role === 'outer') outerWays.push(nodes);
        else if (member.role === 'inner') innerWays.push(nodes);
      }

      if (outerWays.length > 0) {
        const outerRing = stitchRings(outerWays);
        if (outerRing && outerRing.length >= 3) {
          const rt = el.tags;
          buildings.push({
            id:           el.id,
            height:       extractHeight(rt),
            buildingType: rt.building,
            wallColor:    rt['building:colour'] || rt['building:color'] || null,
            roofColor:    rt['roof:colour']     || rt['roof:color']     || null,
            roofShape:    rt['roof:shape']      || null,
            roofHeight:   parseFloat(rt['roof:height']) || null,
            roofMaterial: rt['roof:material']   || null,
            buildingMat:  rt['building:material'] || null,
            nodes:  outerRing,
            holes:  innerWays
              .map(w => stitchRings([w]))
              .filter(r => r && r.length >= 3),
          });
        }
      }
    }
  }

  return { roads, buildings, areas, trees, treeRows, railways, barriers, fountains, coastlines, trafficSignals };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function resolveNodes(nodeIds, nodeMap) {
  if (!nodeIds) return [];
  const result = [];
  for (const id of nodeIds) {
    const node = nodeMap.get(id);
    if (node) result.push(node);
  }
  return result;
}

function extractHeight(tags) {
  if (tags.height) {
    const h = parseFloat(tags.height);
    if (isFinite(h) && h > 0) return h;
  }
  if (tags['building:levels']) {
    const lvl = parseInt(tags['building:levels']);
    if (isFinite(lvl) && lvl > 0) return lvl * 3.5;
  }
  if (tags.levels) {
    const lvl = parseInt(tags.levels);
    if (isFinite(lvl) && lvl > 0) return lvl * 3.5;
  }
  return 10; // default 10 m
}

/**
 * Stitch an array of node-arrays (way segments) into a single closed ring.
 */
function stitchRings(waySegments) {
  if (waySegments.length === 0) return null;
  if (waySegments.length === 1) return waySegments[0];

  const chain     = [...waySegments[0]];
  const remaining = waySegments.slice(1);
  const EPS       = 1e-6;

  while (remaining.length > 0) {
    const tail = chain[chain.length - 1];
    let found  = false;

    for (let i = 0; i < remaining.length; i++) {
      const seg  = remaining[i];
      const head = seg[0];
      const end  = seg[seg.length - 1];

      if (close(tail, head, EPS)) {
        chain.push(...seg.slice(1));
        remaining.splice(i, 1);
        found = true;
        break;
      }
      if (close(tail, end, EPS)) {
        chain.push(...[...seg].reverse().slice(1));
        remaining.splice(i, 1);
        found = true;
        break;
      }
    }
    if (!found) break;
  }

  return chain.length >= 3 ? chain : null;
}

function close(a, b, eps) {
  return Math.abs(a.lat - b.lat) < eps && Math.abs(a.lon - b.lon) < eps;
}

function _resolveAreaType(tags) {
  if (tags.landuse && AREA_LANDUSE.has(tags.landuse)) return tags.landuse;
  if (tags.natural && AREA_NATURAL.has(tags.natural)) return tags.natural;
  if (tags.leisure && AREA_LEISURE.has(tags.leisure)) return tags.leisure;
  if (tags.water || tags.waterway) return 'water';
  return null;
}
